import React, { useState } from 'react';
import dayjs from 'dayjs';

function getHourFromTimeString(timeStr) {
    if (!timeStr) return 0;
    const [h] = timeStr.split(":");
    return parseInt(h, 10);
}

export default function Calendar({ trips, onApproveTrip, onCancelTrip }) {
    const [currentDate, setCurrentDate] = useState(new Date());
    const [viewMode, setViewMode] = useState('week');

    const hebrewDays = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];
    const hours = Array.from({ length: 24 }, (_, i) => i);

    const getTripsForDate = (date) => {
        if (!date) return [];
        return trips.filter(trip => {
            if (trip.scheduledDate) {
                return dayjs(trip.scheduledDate).isSame(date, 'day');
            }
            return false;
        }).sort((a, b) => (a.scheduledTime || "00:00").localeCompare(b.scheduledTime || "00:00"));
    };
    
    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('he-IL', { year: 'numeric', month: 'long', day: 'numeric' });
    };

    const getDaysInMonth = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();
        const startingDayOfWeek = firstDay.getDay();
        const days = [];
        for (let i = 0; i < startingDayOfWeek; i++) { days.push(null); }
        for (let i = 1; i <= daysInMonth; i++) { days.push(new Date(year, month, i)); }
        return days;
    };

    const getWeekDates = (date) => {
        const week = [];
        const dayOfWeek = date.getDay();
        const sunday = new Date(date);
        sunday.setDate(date.getDate() - dayOfWeek);
        for (let i = 0; i < 7; i++) {
            const d = new Date(sunday);
            d.setDate(sunday.getDate() + i);
            week.push(d);
        }
        return week;
    };

    const goToPrevious = () => {
        const newDate = new Date(currentDate);
        if (viewMode === 'month') newDate.setMonth(newDate.getMonth() - 1);
        else if (viewMode === 'week') newDate.setDate(newDate.getDate() - 7);
        else if (viewMode === 'day') newDate.setDate(newDate.getDate() - 1);
        setCurrentDate(newDate);
    };

    const goToNext = () => {
        const newDate = new Date(currentDate);
        if (viewMode === 'month') newDate.setMonth(newDate.getMonth() + 1);
        else if (viewMode === 'week') newDate.setDate(newDate.getDate() + 7);
        else if (viewMode === 'day') newDate.setDate(newDate.getDate() + 1);
        setCurrentDate(newDate);
    };

    const handleSetViewMode = (mode) => {
        if (mode === 'day') {
            setCurrentDate(new Date());
        }
        setViewMode(mode);
    };

    const goToDay = (date) => {
        setCurrentDate(date);
        setViewMode('day');
    };

    const renderTrip = (trip) => {
        const statusClass = trip.status === 'confirmed' ? 'bg-green-600' : trip.status === 'pending' ? 'bg-yellow-600' : 'bg-red-700 opacity-70';
        return (
            <div key={trip.id} className={`text-xs p-1 rounded mb-1 ${statusClass}`}>
              <div>
    <span className="font-semibold">{trip.scheduledTime}</span>

    <span dir="ltr" className="inline-block"> {trip.pickupLocation} → {trip.destination}</span>
</div>
                {trip.status === 'pending' && (
                    <div className="flex gap-1 mt-1">
                        <button onClick={() => onApproveTrip(trip.id)} className="flex-1 bg-green-500 hover:bg-green-400 text-black text-xs font-bold py-0.5 px-1 rounded">אשר</button>
                        <button onClick={() => onCancelTrip(trip.id)} className="flex-1 bg-red-500 hover:bg-red-400 text-white text-xs font-bold py-0.5 px-1 rounded">דחה</button>
                    </div>
                )}
                {trip.status === 'confirmed' && (
                    <div className="flex gap-1 mt-1">
                        <button onClick={() => onCancelTrip(trip.id)} className="w-full bg-gray-500 hover:bg-gray-400 text-white text-xs font-bold py-0.5 px-1 rounded">בטל נסיעה</button>
                    </div>
                )}
            </div>
        );
    };

    let content;
    if (viewMode === 'month') {
        const days = getDaysInMonth(currentDate);
        content = (
            <div className="grid grid-cols-7 gap-1">
                {hebrewDays.map(day => <div key={day} className="p-2 text-center font-semibold text-amber-400">{day}</div>)}
                {days.map((day, index) => {
                    const tripsForDay = day ? getTripsForDate(day) : [];
                    const isToday = day && day.toDateString() === new Date().toDateString();
                    return (
                        <div key={index} className={`relative min-h-[80px] p-1 border border-gray-600 ${day ? 'bg-gray-800' : 'bg-gray-900'} ${isToday ? 'ring-2 ring-amber-400' : ''}`} onDoubleClick={() => day && goToDay(day)}>
                            {day && <div className="text-sm font-semibold mb-1">{day.getDate()}</div>}
                            <div className="absolute inset-0 top-6 left-1 right-1 overflow-hidden space-y-1">
                            {tripsForDay.map(trip => {
                                const statusColor = trip.status === 'confirmed' ? 'bg-green-500' : 'bg-yellow-500';
                                return <div key={trip.id} title={`${trip.scheduledTime} - ${trip.pickupLocation}`} className={`w-full h-1.5 rounded-full ${statusColor}`}></div>
                            })}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    } else if (viewMode === 'week') {
        const week = getWeekDates(currentDate);
        content = (
            <div className="flex w-full min-h-[600px]" style={{ height: '600px' }}>
                <div className="flex flex-col w-[48px] min-w-[48px] bg-gray-900 border border-gray-600 text-xs text-gray-400 text-center">
                    <div className="h-10 border-b border-gray-600"></div>
                    {hours.map(hour => <div key={hour} className="flex-1 border-b border-gray-600 flex items-start justify-center py-0.5">{hour.toString().padStart(2, '0')}:00</div>)}
                </div>
                {week.map((day, idx) => {
                    const tripsForDay = getTripsForDate(day);
                    const isToday = day.toDateString() === new Date().toDateString();
                    return (
                        <div key={idx} className={`flex-1 flex flex-col border border-gray-600 bg-gray-800 mx-0.5 min-w-0 ${isToday ? 'ring-2 ring-amber-400' : ''}`} style={{ minWidth: 0 }} onDoubleClick={() => goToDay(day)}>
                            <div className="text-sm font-semibold mb-1 text-amber-400 text-center border-b border-gray-600 py-2">{hebrewDays[day.getDay()]}</div>
                            <div className="text-base font-bold mb-2 text-center">{day.getDate()}</div>
                            <div className="flex-1 flex flex-col gap-2 px-2 pb-2 overflow-y-auto">{tripsForDay.map(renderTrip)}</div>
                        </div>
                    );
                })}
            </div>
        );
    } else if (viewMode === 'day') {
        const day = currentDate;
        content = (
            <div className="overflow-x-auto">
                <div className="grid grid-cols-[48px_1fr] gap-0 border border-gray-600 bg-gray-800">
                    <div className="bg-gray-900 border-b border-gray-600 w-[48px]"></div>
                    <div className="p-2 text-center font-semibold text-amber-400 border-b border-gray-600">{hebrewDays[day.getDay()]}</div>
                    {hours.map(hour => {
                        const tripsForHour = trips.filter(trip => {
                            if (!trip.scheduledDate || !trip.scheduledTime) return false;
                            const tripDate = new Date(trip.scheduledDate);
                            return tripDate.toDateString() === day.toDateString() && getHourFromTimeString(trip.scheduledTime) === hour;
                        });
                        return (
                            <React.Fragment key={hour}>
                                <div className="border-t border-gray-600 text-xs text-gray-400 px-2 py-1 bg-gray-900 text-center w-[48px] min-w-[48px]">{hour.toString().padStart(2, '0')}:00</div>
                                <div className="border-t border-l border-gray-600 min-h-[32px] px-1 py-0.5 relative bg-gray-800" style={{ minHeight: 32 }}>
                                    {tripsForHour.map(renderTrip)}
                                </div>
                            </React.Fragment>
                        );
                    })}
                </div>
            </div>
        );
    }

    return (
        <div className="bg-gray-700 rounded-lg p-6">
            <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold">לוח שנה</h3>
                <div className="flex gap-2">
                    <button onClick={() => handleSetViewMode('day')} className={`px-3 py-1 rounded text-sm ${viewMode === 'day' ? 'bg-amber-500 text-black' : 'bg-gray-600 text-white'}`}>יום</button>
                    <button onClick={() => handleSetViewMode('week')} className={`px-3 py-1 rounded text-sm ${viewMode === 'week' ? 'bg-amber-500 text-black' : 'bg-gray-600 text-white'}`}>שבוע</button>
                    <button onClick={() => handleSetViewMode('month')} className={`px-3 py-1 rounded text-sm ${viewMode === 'month' ? 'bg-amber-500 text-black' : 'bg-gray-600 text-white'}`}>חודש</button>
                </div>
            </div>
            <div className="flex justify-between items-center mb-4">
                <button onClick={goToPrevious} className="bg-gray-600 hover:bg-gray-500 text-white px-4 py-2 rounded">
                    {viewMode === 'month' ? 'חודש קודם' : viewMode === 'week' ? 'שבוע קודם' : 'יום קודם'}
                </button>
                <h4 className="text-lg font-semibold">{formatDate(currentDate)}</h4>
                <button onClick={goToNext} className="bg-gray-600 hover:bg-gray-500 text-white px-4 py-2 rounded">
                    {viewMode === 'month' ? 'חודש הבא' : viewMode === 'week' ? 'שבוע הבא' : 'יום הבא'}
                </button>
            </div>
            {content}
        </div>
    );
}